/**
 * CategoryFilter Component - Story 7.1: Create Marketplace Homepage
 * Interactive category filter buttons for product browsing
 *
 * Features:
 * - Visual category buttons with icons and counts
 * - Responsive layout (mobile-stacked, desktop-grid)
 * - Keyboard navigation and accessibility
 * - Hover effects and smooth transitions
 * - Optional "View All" button
 * - Tooltip descriptions for each category
 *
 * @component
 * @example
 * // Basic usage
 * <CategoryFilter
 *   onCategorySelect={(category) => filterProducts(category)}
 *   selectedCategory={selectedCategory}
 * />
 *
 * @example
 * // With custom counts and display options
 * <CategoryFilter
 *   onCategorySelect={handleCategoryChange}
 *   selectedCategory="BIOGAS"
 *   counts={{
 *     BIOGAS: 42,
 *     BIOMASS: 18,
 *     SEEDS: 56,
 *     EQUIPMENT: 23,
 *     FERTILIZER: 31,
 *     OTHER: 12,
 *   }}
 *   showCount={true}
 *   showViewAll={true}
 *   displayMode="grid"
 * />
 */

import React, { useMemo } from 'react';
import { Button } from '@/components/ui/button';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { Badge } from '@/components/ui/badge';
import { cn } from '@/lib/utils';
import type { ProductCategory } from '../types';
import {
  getAllCategories,
  getCategoryCountText,
  type CategoryDisplayOptions,
  DEFAULT_DISPLAY_OPTIONS,
} from '../config/categoryConfig';

/**
 * CategoryFilter Props Interface
 */
export interface CategoryFilterProps {
  /** Callback when a category is selected */
  onCategorySelect?: (category: ProductCategory | null) => void;
  /** Currently selected category (null = "View All") */
  selectedCategory?: ProductCategory | null;
  /** Show product counts on buttons */
  showCount?: boolean;
  /** Product counts per category */
  counts?: Partial<Record<ProductCategory, number>>;
  /** Show category tooltips */
  showTooltips?: boolean;
  /** Display mode: inline flex wrap or grid */
  displayMode?: 'inline' | 'grid';
  /** Button size variant */
  buttonSize?: 'sm' | 'default' | 'lg';
  /** Show "View All" button */
  showViewAll?: boolean;
  /** Optional CSS class for container */
  className?: string;
  /** Optional CSS class for button group */
  buttonGroupClassName?: string;
}

/**
 * Category Button Component
 * Individual category button with icon, label, and optional count
 */
interface CategoryButtonProps {
  category: {
    key: ProductCategory;
    icon: React.ComponentType<{ className?: string }>;
    label: string;
    description: string;
    color: string;
    bgColor: string;
    ariaLabel: string;
  };
  isSelected: boolean;
  count?: number;
  showCount: boolean;
  showTooltip: boolean;
  buttonSize: 'sm' | 'default' | 'lg';
  onClick: () => void;
}

/**
 * CategoryButton - Memoized category button component
 */
const CategoryButton: React.FC<CategoryButtonProps> = React.memo(
  ({ category, isSelected, count, showCount, showTooltip, buttonSize, onClick }) => {
    const Icon = category.icon;

    const buttonContent = (
      <Button
        variant={isSelected ? 'default' : 'ghost'}
        size={buttonSize}
        onClick={onClick}
        className={cn(
          'flex items-center gap-2 transition-all duration-200',
          'hover:scale-105 active:scale-95',
          !isSelected && 'hover:bg-accent',
          isSelected && 'shadow-md',
          buttonSize === 'lg' && 'px-6 py-3 text-base',
          buttonSize === 'default' && 'px-4 py-2',
          buttonSize === 'sm' && 'px-3 py-1.5 text-sm'
        )}
        aria-label={category.ariaLabel}
        aria-pressed={isSelected}
      >
        {/* Icon */}
        <Icon
          className={cn(
            'flex-shrink-0 transition-colors',
            buttonSize === 'lg' && 'h-6 w-6',
            buttonSize === 'default' && 'h-5 w-5',
            buttonSize === 'sm' && 'h-4 w-4',
            isSelected ? 'text-primary-foreground' : category.color
          )}
          aria-hidden="true"
        />

        {/* Label */}
        <span className="font-medium">{category.label}</span>

        {/* Count Badge */}
        {showCount && count !== undefined && (
          <Badge
            variant={isSelected ? 'secondary' : 'outline'}
            className={cn(
              'ml-1 transition-colors',
              buttonSize === 'sm' && 'text-xs px-1.5 py-0',
              !isSelected && 'bg-muted'
            )}
          >
            {count}
          </Badge>
        )}
      </Button>
    );

    // Wrap with tooltip if enabled
    if (showTooltip) {
      return (
        <Tooltip>
          <TooltipTrigger asChild>{buttonContent}</TooltipTrigger>
          <TooltipContent side="bottom" className="max-w-xs">
            <p className="text-sm">{category.description}</p>
            {showCount && count !== undefined && (
              <p className="mt-1 text-xs text-muted-foreground">
                {getCategoryCountText(count)}
              </p>
            )}
          </TooltipContent>
        </Tooltip>
      );
    }

    return buttonContent;
  }
);

CategoryButton.displayName = 'CategoryButton';

/**
 * CategoryFilter Component
 * Main component for category filtering interface
 */
export const CategoryFilter: React.FC<CategoryFilterProps> = ({
  onCategorySelect,
  selectedCategory = null,
  showCount = DEFAULT_DISPLAY_OPTIONS.showCount,
  counts = {},
  showTooltips = DEFAULT_DISPLAY_OPTIONS.showTooltips,
  displayMode = DEFAULT_DISPLAY_OPTIONS.displayMode,
  buttonSize = DEFAULT_DISPLAY_OPTIONS.buttonSize,
  showViewAll = DEFAULT_DISPLAY_OPTIONS.showViewAll,
  className,
  buttonGroupClassName,
}) => {
  // Get all categories, memoized
  const categories = useMemo(() => getAllCategories(), []);

  // Track focused index for keyboard navigation
  const [focusedIndex, setFocusedIndex] = React.useState<number>(0);

  // Get all category keys including "View All"
  const allCategoryKeys = useMemo(() => {
    return showViewAll
      ? [null, ...categories.map(c => c.key)]
      : categories.map(c => c.key);
  }, [categories, showViewAll]);

  // Calculate total products count for "View All"
  const totalCount = useMemo(() => {
    return Object.values(counts).reduce((sum, count) => sum + (count || 0), 0);
  }, [counts]);

  // Handle category selection
  const handleCategoryClick = (category: ProductCategory | null) => {
    if (onCategorySelect) {
      onCategorySelect(category);
    }
  };

  // Handle keyboard navigation for radio group
  const handleKeyDown = (
    e: React.KeyboardEvent,
    category: ProductCategory | null
  ) => {
    const currentIndex = allCategoryKeys.indexOf(category);
    let newIndex = currentIndex;

    // Arrow Right or Arrow Down - move to next
    if (e.key === 'ArrowRight' || e.key === 'ArrowDown') {
      e.preventDefault();
      newIndex = (currentIndex + 1) % allCategoryKeys.length;
    }
    // Arrow Left or Arrow Up - move to previous
    else if (e.key === 'ArrowLeft' || e.key === 'ArrowUp') {
      e.preventDefault();
      newIndex = currentIndex - 1 < 0 ? allCategoryKeys.length - 1 : currentIndex - 1;
    }
    // Enter or Space - select category
    else if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      handleCategoryClick(category);
      return;
    }
    // Home - go to first
    else if (e.key === 'Home') {
      e.preventDefault();
      newIndex = 0;
    }
    // End - go to last
    else if (e.key === 'End') {
      e.preventDefault();
      newIndex = allCategoryKeys.length - 1;
    }
    else {
      return; // Not a handled key
    }

    // Move focus to new category
    if (newIndex !== currentIndex) {
      setFocusedIndex(newIndex);
      handleCategoryClick(allCategoryKeys[newIndex]);
    }
  };

  return (
    <TooltipProvider delayDuration={300}>
      <div
        className={cn(
          'w-full',
          'py-6 px-4 md:px-6',
          'bg-background',
          className
        )}
        role="group"
        aria-label="Product category filters"
      >
        {/* Section Header */}
        <div className="mb-4">
          <h2 className="text-lg font-semibold text-foreground">
            Browse by Category
          </h2>
          <p className="mt-1 text-sm text-muted-foreground">
            Filter products by their category
          </p>
        </div>

        {/* Button Group */}
        <div
          className={cn(
            'gap-3',
            displayMode === 'inline' && 'flex flex-wrap',
            displayMode === 'grid' && [
              'grid',
              'grid-cols-2 md:grid-cols-3 lg:grid-cols-6',
              'gap-3',
            ],
            buttonGroupClassName
          )}
          role="radiogroup"
          aria-label="Category filter options"
        >
          {/* View All Button */}
          {showViewAll && (
            <div
              role="radio"
              aria-checked={selectedCategory === null}
              tabIndex={selectedCategory === null ? 0 : -1}
              onKeyDown={(e) => handleKeyDown(e, null)}
            >
              {showTooltips ? (
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Button
                      variant={selectedCategory === null ? 'default' : 'ghost'}
                      size={buttonSize}
                      onClick={() => handleCategoryClick(null)}
                      className={cn(
                        'flex items-center gap-2 transition-all duration-200',
                        'hover:scale-105 active:scale-95',
                        selectedCategory === null ? 'shadow-md' : 'hover:bg-accent',
                        buttonSize === 'lg' && 'px-6 py-3 text-base',
                        buttonSize === 'default' && 'px-4 py-2',
                        buttonSize === 'sm' && 'px-3 py-1.5 text-sm'
                      )}
                      aria-label="View all products"
                      aria-pressed={selectedCategory === null}
                    >
                      <span className="font-medium">View All</span>
                      {showCount && totalCount > 0 && (
                        <Badge
                          variant={selectedCategory === null ? 'secondary' : 'outline'}
                          className={cn(
                            'ml-1 transition-colors',
                            buttonSize === 'sm' && 'text-xs px-1.5 py-0',
                            selectedCategory !== null && 'bg-muted'
                          )}
                        >
                          {totalCount}
                        </Badge>
                      )}
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent side="bottom">
                    <p className="text-sm">View all products across all categories</p>
                    {showCount && totalCount > 0 && (
                      <p className="mt-1 text-xs text-muted-foreground">
                        {getCategoryCountText(totalCount)}
                      </p>
                    )}
                  </TooltipContent>
                </Tooltip>
              ) : (
                <Button
                  variant={selectedCategory === null ? 'default' : 'ghost'}
                  size={buttonSize}
                  onClick={() => handleCategoryClick(null)}
                  className={cn(
                    'flex items-center gap-2 transition-all duration-200',
                    'hover:scale-105 active:scale-95',
                    selectedCategory === null ? 'shadow-md' : 'hover:bg-accent',
                    buttonSize === 'lg' && 'px-6 py-3 text-base',
                    buttonSize === 'default' && 'px-4 py-2',
                    buttonSize === 'sm' && 'px-3 py-1.5 text-sm'
                  )}
                  aria-label="View all products"
                  aria-pressed={selectedCategory === null}
                >
                  <span className="font-medium">View All</span>
                  {showCount && totalCount > 0 && (
                    <Badge
                      variant={selectedCategory === null ? 'secondary' : 'outline'}
                      className={cn(
                        'ml-1 transition-colors',
                        buttonSize === 'sm' && 'text-xs px-1.5 py-0',
                        selectedCategory !== null && 'bg-muted'
                      )}
                    >
                      {totalCount}
                    </Badge>
                  )}
                </Button>
              )}
            </div>
          )}

          {/* Category Buttons */}
          {categories.map((category) => (
            <div
              key={category.key}
              role="radio"
              aria-checked={selectedCategory === category.key}
              tabIndex={selectedCategory === category.key ? 0 : -1}
              onKeyDown={(e) => handleKeyDown(e, category.key)}
            >
              <CategoryButton
                category={category}
                isSelected={selectedCategory === category.key}
                count={counts[category.key]}
                showCount={showCount}
                showTooltip={showTooltips}
                buttonSize={buttonSize}
                onClick={() => handleCategoryClick(category.key)}
              />
            </div>
          ))}
        </div>
      </div>
    </TooltipProvider>
  );
};

// Display name for debugging
CategoryFilter.displayName = 'CategoryFilter';

export default CategoryFilter;
